// ============================================================================
// TEST PANE — Postman-like HTTP request builder
//
// Replaces the basic TestRunModal. For REST integrations, it sends real HTTP
// requests to the platform endpoint with full request/response viewing.
// For non-REST integrations, it falls back to the existing ActivityDrawer.
//
// Pre-fills method, URL, headers, and body from the integration's OpenAPI spec
// fetched via GET /api/admin/openapi/integrations/{id}.
//
// Depends on: API(), rawFetch(), fmtDate, fmtMs (globals from main script).
// React loaded as a global from CDN.
// ============================================================================

// `var`, not `const`: every text/babel script shares one global scope, so a second
// `const` binding of these names is a redeclaration SyntaxError now that Babel compiles
// JSX only (data-presets="react") instead of downlevelling to ES5.
var {useState,useEffect,useCallback,useRef,useMemo}=React;

// ── Method color map ────────────────────────────────────────────────────────
const METHOD_COLORS={
  GET:'#16a34a',POST:'#2563eb',PUT:'#d97706',PATCH:'#9333ea',
  DELETE:'#dc2626',HEAD:'#64748b',OPTIONS:'#64748b'
};
const METHODS=['GET','POST','PUT','PATCH','DELETE','HEAD','OPTIONS'];

// ── Syntax highlight JSON body ──────────────────────────────────────────────
function syntaxJson(raw){
  if(raw==null||raw==='') return '';
  let s=typeof raw==='string'?raw:JSON.stringify(raw,null,2);
  s=s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  s=s.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
    match=>{
      let cls='json-num';
      if(/^"/.test(match)) cls=/:$/.test(match)?'json-key':'json-str';
      else if(/true|false/.test(match)) cls='json-bool';
      else if(/null/.test(match)) cls='json-null';
      return `<span class="${cls}">${match}</span>`;
    });
  return s;
}

// ── Format bytes for size display ───────────────────────────────────────────
function fmtBytes(n){
  if(n<1024) return n+' B';
  if(n<1048576) return (n/1024).toFixed(1)+' KB';
  return (n/1048576).toFixed(1)+' MB';
}

// ── Simple key-value row editor (used for Params and Headers tabs) ──────────
function KeyValueEditor({rows,onRowsChange,placeholderKey,placeholderVal}){
  const addRow=()=>onRowsChange([...rows,{key:'',value:'',enabled:true}]);
  const updateRow=(idx,field,val)=>{
    const next=[...rows];
    next[idx]={...next[idx],[field]:val};
    onRowsChange(next);
  };
  const removeRow=(idx)=>onRowsChange(rows.filter((_,i)=>i!==idx));
  const toggleRow=(idx)=>onRowsChange(rows.map((r,i)=>i===idx?{...r,enabled:!r.enabled}:r));

  if(!rows.length) rows=[{key:'',value:'',enabled:true}];

  return(
    <div style={{display:'flex',flexDirection:'column',gap:4}}>
      {rows.map((r,idx)=>(
        <div key={idx} style={{display:'flex',gap:4,alignItems:'center',opacity:r.enabled?1:0.4}}>
          <button onClick={()=>toggleRow(idx)} title={r.enabled?'Disable':'Enable'}
            style={{background:'none',border:'none',cursor:'pointer',color:r.enabled?'var(--text2)':'var(--text3)',padding:2,fontSize:11,flexShrink:0}}>
            {r.enabled?'◉':'○'}
          </button>
          <input className="form-input" value={r.key} onChange={e=>updateRow(idx,'key',e.target.value)}
            placeholder={placeholderKey||'Key'} style={{flex:1,minWidth:0,fontSize:12}}/>
          <input className="form-input" value={r.value} onChange={e=>updateRow(idx,'value',e.target.value)}
            placeholder={placeholderVal||'Value'} style={{flex:1.5,minWidth:0,fontSize:12}}/>
          <button onClick={()=>removeRow(idx)} title="Remove"
            style={{background:'none',border:'none',cursor:'pointer',color:'var(--text3)',padding:2,fontSize:14,flexShrink:0}}>×</button>
        </div>
      ))}
      <button className="btn btn-ghost btn-xs" onClick={addRow} style={{alignSelf:'flex-start',fontSize:11}}>+ Add row</button>
    </div>
  );
}

// ── Body type selector + editor ────────────────────────────────────────────
function BodyEditor({body,onBodyChange,bodyType,onBodyTypeChange}){
  return(
    <div style={{display:'flex',flexDirection:'column',gap:8}}>
      <div style={{display:'flex',gap:6,alignItems:'center'}}>
        <label style={{fontSize:11,color:'var(--text2)',fontWeight:600}}>Type:</label>
        {['none','json','text','form-data','x-www-form-urlencoded'].map(t=>(
          <button key={t} onClick={()=>{onBodyTypeChange(t);if(t==='none')onBodyChange('');}}
            className={bodyType===t?'btn btn-primary btn-xs':'btn btn-ghost btn-xs'}
            style={{fontSize:10,textTransform:'none'}}>{t}</button>
        ))}
      </div>
      {bodyType==='json'&&(
        <textarea className="form-input" rows={10} value={body}
          onChange={e=>onBodyChange(e.target.value)}
          placeholder='{"key": "value"}'
          style={{fontFamily:'ui-monospace,monospace',fontSize:12,lineHeight:1.5,resize:'vertical'}}/>
      )}
      {bodyType==='text'&&(
        <textarea className="form-input" rows={8} value={body}
          onChange={e=>onBodyChange(e.target.value)}
          placeholder="Plain text body..."
          style={{fontFamily:'ui-monospace,monospace',fontSize:12,lineHeight:1.5,resize:'vertical'}}/>
      )}
      {(bodyType==='form-data'||bodyType==='x-www-form-urlencoded')&&(
        <KeyValueEditor
          rows={useMemo(()=>{
            try{return JSON.parse(body||'[]');}catch{return[];}
          },[body])}
          onRowsChange={rows=>onBodyChange(JSON.stringify(rows))}
          placeholderKey="Field name"
          placeholderVal="Value"
        />
      )}
    </div>
  );
}

// ── Auth tab ────────────────────────────────────────────────────────────────
function AuthEditor({headers,onHeadersChange,integration}){
  const [authType,setAuthType]=useState('none');
  const [apiKeyVal,setApiKeyVal]=useState('');
  const [bearerToken,setBearerToken]=useState('');

  useEffect(()=>{
    // Detect existing auth headers
    const xApiKey=headers.find(h=>h.key.toLowerCase()==='x-api-key'&&h.enabled);
    const authH=headers.find(h=>h.key.toLowerCase()==='authorization'&&h.enabled);
    if(xApiKey){setAuthType('apikey');setApiKeyVal(xApiKey.value);}
    if(authH&&authH.value.startsWith('Bearer ')){setAuthType('bearer');setBearerToken(authH.value.slice(7));}
  },[]);

  const applyApiKey=()=>{
    const next=headers.filter(h=>h.key.toLowerCase()!=='x-api-key');
    if(apiKeyVal) next.push({key:'X-API-Key',value:apiKeyVal,enabled:true});
    onHeadersChange(next);
  };
  const applyBearer=()=>{
    const next=headers.filter(h=>h.key.toLowerCase()!=='authorization');
    if(bearerToken) next.push({key:'Authorization',value:'Bearer '+bearerToken,enabled:true});
    onHeadersChange(next);
  };

  return(
    <div style={{display:'flex',flexDirection:'column',gap:12}}>
      <select value={authType} onChange={e=>setAuthType(e.target.value)}
        className="form-input" style={{fontSize:12,maxWidth:240}}>
        <option value="none">No auth</option>
        <option value="apikey">API Key (X-API-Key)</option>
        <option value="bearer">Bearer Token</option>
      </select>
      {authType==='apikey'&&(
        <div style={{display:'flex',gap:6}}>
          <input className="form-input" value={apiKeyVal} onChange={e=>setApiKeyVal(e.target.value)}
            placeholder="API key value..." style={{flex:1,fontSize:12}}/>
          <button className="btn btn-primary btn-sm" onClick={applyApiKey}>Apply</button>
        </div>
      )}
      {authType==='bearer'&&(
        <div style={{display:'flex',gap:6}}>
          <input className="form-input" value={bearerToken} onChange={e=>setBearerToken(e.target.value)}
            placeholder="JWT or API key..." style={{flex:1,fontSize:12}}/>
          <button className="btn btn-primary btn-sm" onClick={applyBearer}>Apply</button>
        </div>
      )}
      {(integration||{}).builderClassName&&(
        <div style={{fontSize:11,color:'var(--text3)',padding:'8px 10px',background:'var(--bg)',borderRadius:6,border:'1px solid var(--border)'}}>
          This integration uses Java DSL ({((integration||{}).builderClassName||'').split('.').pop()}). Auth is handled by the route — see the integration's source for details.
        </div>
      )}
    </div>
  );
}

// ── Main TestPane component ─────────────────────────────────────────────────
function TestPane({integration,onClose,showToast,connections,projects}){
  const isRest=(integration||{}).triggerType==='REST';
  const isActive=(integration||{}).status==='ACTIVE';
  const isJava=!!((integration||{}).builderClassName&&(integration||{}).builderClassName.length>0);

  // ── URL + method state ───────────────────────────────────────────────────
  const [method,setMethod]=useState('POST');
  const [url,setUrl]=useState('');
  const [specNote,setSpecNote]=useState(null);

  // ── Tab state ────────────────────────────────────────────────────────────
  const [activeTab,setActiveTab]=useState('body');
  const TABS=['params','headers','body','auth'];

  // ── Request builders ────────────────────────────────────────────────────
  const [params,setParams]=useState([]);
  const [headers,setHeaders]=useState([{key:'Content-Type',value:'application/json',enabled:true}]);
  const [bodyText,setBodyText]=useState('{}');
  const [bodyType,setBodyType]=useState('json');

  // ── Response state ───────────────────────────────────────────────────────
  const [respStatus,setRespStatus]=useState(null);
  const [respTime,setRespTime]=useState(null);
  const [respBody,setRespBody]=useState(null);
  const [respHeaders,setRespHeaders]=useState([]);
  const [respSize,setRespSize]=useState(0);
  const [respError,setRespError]=useState(null);
  const [sending,setSending]=useState(false);

  // ── Fetch OpenAPI spec on mount ──────────────────────────────────────────
  useEffect(()=>{
    if(!integration||!integration.id) return;
    let alive=true;
    fetch('/api/admin/openapi/integrations/'+integration.id)
      .then(r=>r.ok?r.json():Promise.reject(r.statusText))
      .then(spec=>{
        if(!alive) return;
        // Extract path from spec
        const paths=spec.paths||{};
        const pathKeys=Object.keys(paths);
        if(pathKeys.length){
          const path=pathKeys[0];
          setUrl(window.location.origin+'/'+path.replace(/^\//,''));
          // Detect method from spec
          const methods=Object.keys(paths[path]||{});
          if(methods.includes('post')) setMethod('POST');
          else if(methods.length) setMethod(methods[0].toUpperCase());
          // Pre-fill auth headers if spec declares security
          const op=paths[path][methods[0]];
          const sec=(op||{}).security;
          if(sec&&sec.length){
            const schemes=sec[0]||{};
            if(schemes.apiKeyAuth!==undefined){
              setHeaders(hs=>[...hs.filter(h=>h.key.toLowerCase()!=='x-api-key'),{key:'X-API-Key',value:'<your-api-key>',enabled:true}]);
            }
            if(schemes.httpBearer!==undefined){
              setHeaders(hs=>[...hs.filter(h=>h.key.toLowerCase()!=='authorization'),{key:'Authorization',value:'Bearer <token>',enabled:true}]);
            }
          }
        }
        if(spec.note){
          setSpecNote(spec.note);
          setActiveTab('body');
        }
      })
      .catch(()=>{/* spec not available, user fills manually */});
    return()=>{alive=false;};
  },[(integration||{}).id]);

  // ── Build request ────────────────────────────────────────────────────────
  const buildRequest=useCallback(async()=>{
    if(!url){showToast('Enter a URL','error');return;}
    setSending(true);
    setRespStatus(null);setRespTime(null);setRespBody(null);setRespError(null);setRespSize(0);

    const t0=performance.now();

    // Build query string from params
    let targetUrl=url;
    const enabledParams=params.filter(p=>p.enabled&&p.key);
    if(enabledParams.length){
      const sep=targetUrl.includes('?')?'&':'?';
      targetUrl+=sep+enabledParams.map(p=>encodeURIComponent(p.key)+'='+encodeURIComponent(p.value)).join('&');
    }

    // Build headers
    const reqHeaders=new Headers();
    const enabledHeaders=headers.filter(h=>h.enabled&&h.key);
    enabledHeaders.forEach(h=>reqHeaders.append(h.key,h.value));

    // Build body
    let reqBody=undefined;
    if(bodyType==='json') reqBody=bodyText;
    else if(bodyType==='text') reqBody=bodyText;
    else if(bodyType==='form-data'||bodyType==='x-www-form-urlencoded'){
      const rows=params.filter(p=>p.enabled&&p.key);
      if(bodyType==='form-urlencoded'){
        reqBody=rows.map(p=>encodeURIComponent(p.key)+'='+encodeURIComponent(p.value)).join('&');
        reqHeaders.set('Content-Type','application/x-www-form-urlencoded');
      }else{
        const fd=new FormData();
        rows.forEach(p=>fd.append(p.key,p.value));
        reqBody=fd;
        reqHeaders.delete('Content-Type'); // browser sets multipart boundary
      }
    }

    try{
      const resp=await fetch(targetUrl,{
        method:method.toUpperCase(),
        headers:reqHeaders,
        body:['GET','HEAD'].includes(method.toUpperCase())?undefined:reqBody,
        mode:'cors',
        credentials:'same-origin',
      });

      const ms=Math.round(performance.now()-t0);
      setRespTime(ms);

      const respHeadersArr=[...resp.headers.entries()];
      setRespHeaders(respHeadersArr);

      const ct=(resp.headers.get('content-type')||'').toLowerCase();
      const isJson=ct.includes('application/json');
      let body=null;
      const raw=await resp.text();
      setRespSize(new Blob([raw]).size);
      try{ body=isJson?JSON.parse(raw):raw; }catch{ body=raw; }
      setRespBody(body);
      setRespStatus(resp.status);
    }catch(e){
      setRespError(e.message);
      setRespTime(Math.round(performance.now()-t0));
    }finally{
      setSending(false);
    }
  },[url,method,params,headers,bodyText,bodyType,showToast]);

  // ── Run backend test (for non-REST integrations) ─────────────────────────
  const runBackendTest=async()=>{
    setSending(true);
    setRespStatus(null);setRespTime(null);setRespBody(null);setRespError(null);
    setActiveTab('body');
    try{
      const t0=performance.now();
      const result=await API('POST',`/api/integrations/${integration.id}/test`,bodyText||'{}');
      setRespTime(Math.round(performance.now()-t0));
      setRespStatus(200);
      setRespBody(result);
      setRespSize(new Blob([JSON.stringify(result)]).size);
    }catch(e){
      setRespError(e.message);
      setRespTime(Math.round(performance.now()-t0));
    }finally{
      setSending(false);
    }
  };

  const statusColor=respStatus?(
    respStatus<300?'#16a34a':
    respStatus<400?'#d97706':
    respStatus<500?'#ea580c':'#dc2626'
  ):'var(--text3)';

  return(
    <div style={{position:'fixed',inset:0,background:'rgba(15,17,35,.5)',backdropFilter:'blur(3px)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1050,padding:16}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:'var(--surface)',borderRadius:14,width:'100%',maxWidth:900,maxHeight:'92vh',boxShadow:'var(--shadow-lg)',border:'1px solid var(--border)',display:'flex',flexDirection:'column',overflow:'hidden'}}>

        {/* ── HEADER ─────────────────────────────────────────────────────── */}
        <div style={{padding:'12px 18px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
          <span style={{fontSize:20,display:'inline-flex'}}>
            {isRest?(
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
            ):(
              <BeakerActionIcon size={20} color="var(--accent)"/>
            )}
          </span>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontWeight:800,fontSize:14}}>{isRest?'Test Endpoint':'Debug Test'} · {integration.name}</div>
            <div style={{fontSize:11,color:'var(--text3)',marginTop:1}}>
              {isRest?(
                <>{isJava?'Java DSL':'YAML'} route · {isActive?'ACTIVE':'not ACTIVE'}{isJava?' · '+(integration.builderClassName||'').split('.').pop():''}</>
              ):(
                <>Non-REST trigger ({integration.triggerType}) · backend test runner</>
              )}
            </div>
          </div>
          <div style={{display:'flex',gap:6,alignItems:'center'}}>
            {!isRest&&isActive&&(
              <button className="btn btn-primary btn-sm" onClick={runBackendTest} disabled={sending}
                style={{fontSize:12}}>
                {sending?<><Spinner/> Running…</>:<><BeakerActionIcon size={13}/> Run Test</>}
              </button>
            )}
            <button onClick={onClose} className="btn btn-ghost btn-sm" style={{fontSize:14}}>
              <CloseIcon size={14}/>
            </button>
          </div>
        </div>

        {/* ── REQUEST SECTION ────────────────────────────────────────────── */}
        {isRest&&(
          <>
            {/* URL bar */}
            <div style={{padding:'10px 16px',borderBottom:'1px solid var(--border)',display:'flex',gap:8,alignItems:'center',flexShrink:0,background:'var(--bg)'}}>
              <select value={method} onChange={e=>setMethod(e.target.value)}
                style={{fontSize:13,fontWeight:800,color:METHOD_COLORS[method]||'var(--text)',
                  border:'1px solid var(--border)',borderRadius:6,padding:'6px 10px',background:'var(--surface)',
                  cursor:'pointer',minWidth:90,fontFamily:'ui-monospace,monospace'}}>
                {METHODS.map(m=><option key={m} value={m}>{m}</option>)}
              </select>
              <input className="form-input" value={url} onChange={e=>setUrl(e.target.value)}
                placeholder="https://api.example.com/endpoint"
                style={{flex:1,fontSize:13,fontFamily:'ui-monospace,monospace',padding:'8px 12px'}}/>
              <button className="btn btn-primary" onClick={buildRequest} disabled={sending||!url}
                style={{fontWeight:700,minWidth:80,flexShrink:0}}>
                {sending?<><Spinner/> Sending</>:<>Send</>}
              </button>
            </div>

            {/* Tabs */}
            <div style={{display:'flex',borderBottom:'1px solid var(--border)',flexShrink:0,padding:'0 16px',gap:0,background:'var(--bg)'}}>
              {TABS.map(t=>(
                <button key={t} onClick={()=>setActiveTab(t)}
                  style={{padding:'8px 14px',fontSize:12,fontWeight:600,textTransform:'uppercase',letterSpacing:0.5,
                    border:'none',borderBottom:activeTab===t?'2px solid var(--accent)':'2px solid transparent',
                    background:'transparent',color:activeTab===t?'var(--text)':'var(--text3)',
                    cursor:'pointer',borderRadius:0,transition:'all 0.15s'}}>
                  {t==='form-data'?'Form Data':t}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div style={{padding:'14px 16px',borderBottom:'1px solid var(--border)',flexShrink:0,maxHeight:260,overflow:'auto'}}>
              {activeTab==='params'&&(
                <div>
                  <div style={{fontSize:11,color:'var(--text3)',marginBottom:6}}>Query parameters sent in the URL.</div>
                  <KeyValueEditor rows={params} onRowsChange={setParams}
                    placeholderKey="Parameter name" placeholderVal="Value"/>
                </div>
              )}
              {activeTab==='headers'&&(
                <div>
                  <div style={{fontSize:11,color:'var(--text3)',marginBottom:6}}>Custom request headers.</div>
                  <KeyValueEditor rows={headers} onRowsChange={setHeaders}
                    placeholderKey="Header name" placeholderVal="Value"/>
                </div>
              )}
              {activeTab==='body'&&(
                <BodyEditor body={bodyText} onBodyChange={setBodyText}
                  bodyType={bodyType} onBodyTypeChange={setBodyType}/>
              )}
              {activeTab==='auth'&&(
                <AuthEditor headers={headers} onHeadersChange={setHeaders} integration={integration}/>
              )}
            </div>
          </>
        )}

        {/* ── SPEC NOTE (for non-REST or non-active) ─────────────────────── */}
        {!isRest&&specNote&&(
          <div style={{padding:'10px 16px',borderBottom:'1px solid var(--border)',fontSize:12,color:'var(--text2)',background:'rgba(245,158,11,0.06)'}}>
            {specNote}
          </div>
        )}

        {/* ── RESPONSE SECTION ───────────────────────────────────────────── */}
        <div style={{flex:1,display:'flex',flexDirection:'column',minHeight:0,overflow:'hidden'}}>
          {/* Response status bar */}
          {respStatus!==null||respError?(
            <div style={{padding:'8px 16px',borderBottom:'1px solid var(--border)',display:'flex',gap:16,alignItems:'center',flexShrink:0,background:'var(--bg)'}}>
              {respStatus!==null?(
                <>
                  <span style={{fontSize:13,fontWeight:800,color:statusColor,fontFamily:'ui-monospace,monospace'}}>
                    {respStatus} {respStatusText}
                  </span>
                  <span style={{fontSize:11,color:'var(--text3)'}}>
                    {respTime}ms · {fmtBytes(respSize)}
                  </span>
                </>
              ):(
                <span style={{fontSize:13,fontWeight:700,color:'#dc2626'}}>Error: {respError}</span>
              )}
              {respBody!=null&&(
                <button className="btn btn-ghost btn-xs" onClick={()=>{
                  navigator.clipboard.writeText(typeof respBody==='string'?respBody:JSON.stringify(respBody,null,2));
                  showToast('Response copied','success');
                }} style={{marginLeft:'auto',fontSize:10}}>Copy</button>
              )}
            </div>
          ):(
            !respError&&!sending&&isRest&&(
              <div style={{padding:'20px 16px',textAlign:'center',color:'var(--text3)',fontSize:12}}>
                Fill in the request above and click Send to test this endpoint.
                {!isActive&&' Note: the integration must be ACTIVE to receive requests.'}
              </div>
            )
          )}

          {/* Response body */}
          {respBody!=null&&(
            <div style={{flex:1,overflow:'auto',padding:'12px 16px',background:'#0d1117'}}>
              <pre style={{margin:0,fontFamily:'ui-monospace,monospace',fontSize:12,lineHeight:1.6,color:'#e6edf3',
                whiteSpace:'pre-wrap',wordBreak:'break-word'}}>
                <code dangerouslySetInnerHTML={{__html:syntaxJson(respBody)}}/>
              </pre>
            </div>
          )}

          {/* Response headers (collapsible) */}
          {respHeaders.length>0&&(
            <details style={{borderTop:'1px solid var(--border)',flexShrink:0}}>
              <summary style={{padding:'6px 16px',fontSize:11,fontWeight:600,color:'var(--text2)',cursor:'pointer',userSelect:'none'}}>
                Response Headers ({respHeaders.length})
              </summary>
              <div style={{padding:'0 16px 10px',background:'var(--bg)',maxHeight:120,overflow:'auto'}}>
                {respHeaders.map(([k,v])=>(
                  <div key={k} style={{fontSize:11,fontFamily:'ui-monospace,monospace',display:'flex',gap:8,padding:'2px 0',color:'var(--text2)'}}>
                    <span style={{color:'#7c3aed',minWidth:140}}>{k}:</span>
                    <span style={{color:'var(--text)'}}>{v}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        {/* ── FOOTER ──────────────────────────────────────────────────────── */}
        <div style={{padding:'8px 16px',borderTop:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center',flexShrink:0,fontSize:11,color:'var(--text3)'}}>
          <span>
            {(integration.id||'').slice(0,8)}… · {integration.triggerType} · {integration.status}
            {integration.builderClassName&&<span> · Java DSL</span>}
          </span>
          <span>Powered by OpenAPI spec</span>
        </div>
      </div>
    </div>
  );
}

// ── JSON syntax-highlight style block (injected once) ────────────────────────
const _testPaneStyleInjected=typeof window!=='undefined'?window.__testPaneStyle:false;
if(!_testPaneStyleInjected){
  const s=document.createElement('style');
  s.textContent=`
    .json-key{color:#7c3aed}.json-str{color:#16a34a}.json-num{color:#2563eb}.json-bool{color:#d97706}.json-null{color:#dc2626}
    .form-input{width:100%;padding:7px 10px;border:1px solid var(--border);border-radius:6px;background:var(--surface);
      color:var(--text);font-size:13px;outline:none;transition:border-color .15s;box-sizing:border-box}
    .form-input:focus{border-color:var(--accent);box-shadow:0 0 0 2px color-mix(in srgb,var(--accent) 15%,transparent)}
    .btn{display:inline-flex;align-items:center;gap:5px;padding:7px 14px;border:1px solid var(--border);border-radius:6px;
      cursor:pointer;font-size:13px;font-weight:500;transition:all .15s;white-space:nowrap}
    .btn-primary{background:var(--accent);color:#fff;border-color:var(--accent)}
    .btn-primary:hover:not(:disabled){filter:brightness(1.1)}
    .btn-secondary{background:var(--bg);color:var(--text)}
    .btn-ghost{background:transparent;color:var(--text2);border-color:transparent}
    .btn-ghost:hover{background:var(--bg);color:var(--text)}
    .btn-sm{padding:4px 8px;font-size:11px;border-radius:4px}
    .btn-xs{padding:2px 7px;font-size:10px;border-radius:3px}
    .btn:disabled{opacity:.5;cursor:not-allowed}
  `;
  document.head.appendChild(s);
  if(typeof window!=='undefined') window.__testPaneStyle=true;
}
